import { css } from '@emotion/react';
import { Link } from 'gatsby';
import { useCallback, useMemo } from 'react';
import { getScreen } from '@/styles/media-query';
import { PageProvider } from '@/types/page-provider';

type Props = {
  totalCount: number;
  perPage: number;
  currentPage: number;
  pageProvider: PageProvider;
} & JSX.IntrinsicElements['div'];

export const PaginationNav: React.FC<Props> = ({
  totalCount,
  perPage,
  currentPage,
  pageProvider,
  ...props
}) => {
  const pageLength = Math.ceil(totalCount / perPage);

  const start = useMemo(() => {
    if (currentPage - 1 === 0) return 1;
    if (currentPage - 1 === pageLength - 1) return pageLength - 2;
    return currentPage - 1;
  }, [currentPage, pageLength]);
  const end = useMemo(() => start + 2, [start]);
  const range = useMemo(() => {
    return [...Array(end - start + 1)]
      .map((_, i) => start + i)
      .filter((page) => {
        if (page <= 1) return false;
        if (page >= pageLength) return false;
        return true;
      });
  }, [end, pageLength, start]);

  const prevHref = useMemo(() => {
    if (currentPage <= 1) return null;
    if (currentPage === 2) return pageProvider.$url();
    return pageProvider.page._num(currentPage - 1).$url();
  }, [currentPage, pageProvider]);

  const nextHref = useMemo(() => {
    if (currentPage >= pageLength) return null;
    return pageProvider.page._num(currentPage + 1).$url();
  }, [currentPage, pageLength, pageProvider.page]);

  const isCurrent = useCallback(
    (page: number) => currentPage === page,
    [currentPage],
  );

  return (
    <nav
      css={css`
        display: flex;
        align-items: center;
        justify-content: center;
        gap: var(--space-1);
      `}
      {...props}
    >
      {prevHref ? (
        <Link
          css={css`
            @media ${getScreen('pc')} {
              margin-right: 20px;
            }
          `}
          to={prevHref}
        >
          <i
            css={css`
              width: 44px;
              height: 44px;
              display: block;
              background: linear-gradient(180deg, #00b3a7 0%, #00c9be 100%);
              clip-path: path(
                'M10 24.1789C10 23.6866 10.1816 23.2117 10.51 22.8449L18.764 13.6169C18.9666 13.3828 19.2223 13.2004 19.5096 13.0851C19.797 12.9698 20.1077 12.9248 20.416 12.9539L14.046 24.1789L20.416 35.4029C20.1082 35.4326 19.7977 35.3884 19.5103 35.274C19.223 35.1596 18.9671 34.9782 18.764 34.7449L10.51 25.5129C10.1816 25.1462 10 24.6712 10 24.1789V24.1789Z'
              );
              overflow: hidden;
              white-space: nowrap;
              text-indent: 44px;
            `}
          >
            前へ
          </i>
        </Link>
      ) : (
        <span
          css={css`
            width: 44px;
            height: 44px;
            display: block;

            @media ${getScreen('pc')} {
              margin-right: 20px;
            }
          `}
        ></span>
      )}

      <PaginationItem
        current={isCurrent(1)}
        item={1}
        href={pageProvider.$url()}
      />

      {range.length > 0 && range[0] !== 2 && <Separator />}

      {range.map((item) => {
        return (
          <PaginationItem
            key={item}
            current={isCurrent(item)}
            item={item}
            href={pageProvider.page._num(item).$url()}
          />
        );
      })}

      {range.length > 0 && range[range.length - 1] !== pageLength - 1 && (
        <Separator />
      )}

      {pageLength > 0 && (
        <PaginationItem
          current={isCurrent(pageLength)}
          item={pageLength}
          href={pageProvider.page._num(pageLength).$url()}
        />
      )}

      {nextHref ? (
        <Link
          css={css`
            @media ${getScreen('pc')} {
              margin-left: 20px;
            }
          `}
          to={nextHref}
        >
          <i
            css={css`
              width: 44px;
              height: 44px;
              display: block;
              background: linear-gradient(180deg, #00b3a7 0%, #00c9be 100%);
              clip-path: path(
                'M34 24.0003C34 23.5182 33.8256 23.0531 33.5104 22.694L25.586 13.6577C25.3915 13.4284 25.1461 13.2498 24.8702 13.1369C24.5943 13.024 24.296 12.9799 24 13.0084L30.1156 24.0003L24 34.9912C24.2956 35.0203 24.5937 34.977 24.8695 34.8649C25.1454 34.7529 25.391 34.5753 25.586 34.3469L33.5104 25.3066C33.8256 24.9475 34 24.4824 34 24.0003V24.0003Z'
              );
              overflow: hidden;
              white-space: nowrap;
              text-indent: 44px;
            `}
          >
            次へ
          </i>
        </Link>
      ) : (
        <span
          css={css`
            width: 44px;
            height: 44px;
            display: block;

            @media ${getScreen('pc')} {
              margin-left: 20px;
            }
          `}
        ></span>
      )}
    </nav>
  );
};

const PaginationItem: React.FC<{
  current?: boolean;
  item: number;
  href: string;
}> = ({ current, item, href }) => {
  if (current) {
    return (
      <span css={paginationItemStyle} aria-current="page">
        {item}
      </span>
    );
  }

  return (
    <Link css={paginationItemStyle} to={href}>
      {item}
    </Link>
  );
};

const paginationItemStyle = css`
  display: flex;
  width: 44px;
  height: 44px;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  text-decoration: none;
  font: normal normal bold 14px/20px 'Noto Sans JP', sans-serif;
  letter-spacing: 1.4px;
  color: #5a5b6a;

  &[aria-current] {
    background-color: #00b3a7;
    color: #fff;
    cursor: default;
  }
`;

const Separator: React.FC = () => {
  return (
    <span
      css={css`
        width: 32px;
        height: 1px;
        margin: 0 8px;
        background-color: #00b3a7;
      `}
    ></span>
  );
};
